import {
  BoxProps,
  Flex,
  HStack,
  Icon,
  IconButton,
  IconButtonProps,
  Spacer,
  StackProps,
  Tooltip,
  useBreakpointValue,
} from '@chakra-ui/react'
import { atom, useAtom } from 'jotai'
import React, { ReactNode, useMemo } from 'react'
import IconPanelHide from '~/assets/svgs/icon-panel-hide.svg?react'
import IconPanelShow from '~/assets/svgs/icon-panel-show.svg?react'
import { useUserClickedOutsideElementBoundingBox } from '~/hooks/useUserClickedOutsideElementBoundingBox'
import { themeColorSectionKeys } from '~/themes/colors/colors'

import { useDetailPanelStyle } from './componentStyles/DetailPanel.style'
import { useHeaderStyle } from './componentStyles/Header.style'
import {
  ListPanelVariants,
  useListPanelStyles,
} from './componentStyles/ListPanel.style'
import {
  ToggleListButtonVariants,
  useToggleListButtonStyles,
} from './componentStyles/ToggleListButton.style'

const { Tooltip: TooltipColors } = themeColorSectionKeys.shared

export const SlidingPanelBreak = { base: true, sm: false }

const togglePanelAtom = atom({ isOpen: false, isDisabled: false })
interface UseTogglePanelProps {
  isDisabled: boolean
}
export const useTogglePanel = (
  { isDisabled }: UseTogglePanelProps = { isDisabled: false },
) => {
  const [togglePanelState, setTogglePanelState] = useAtom(togglePanelAtom)

  if (isDisabled !== togglePanelState.isDisabled) {
    setTogglePanelState((prev) => {
      return { ...prev, isDisabled }
    })
  }

  const togglePanelVisibility = (open?: boolean) => {
    if (isDisabled) {
      return
    }
    setTogglePanelState((prev) => {
      return { ...togglePanelState, isOpen: open ?? !prev.isOpen }
    })
  }
  return { state: togglePanelState, togglePanelVisibility }
}

export const QuickActionStack = ({ children, ...rest }: StackProps) => {
  return (
    <HStack gap="0.75rem" paddingRight="1.5rem" {...rest}>
      {children}
    </HStack>
  )
}

interface HeaderProps extends BoxProps {}
export const Header = ({ children, ...rest }: HeaderProps) => {
  const style = useHeaderStyle()

  return (
    <Flex sx={style} {...rest}>
      {children}
    </Flex>
  )
}

type ToggleListButtonProps = {
  clickBehavior?: 'always-open' | 'always-close'
  hidden?: boolean
} & Omit<IconButtonProps, 'aria-label'>
export const ToggleListButton = ({
  hidden,
  clickBehavior,
  ...rest
}: ToggleListButtonProps) => {
  const [togglePanelState, setTogglePanelState] = useAtom(togglePanelAtom)
  const slidingPanelBreak = useBreakpointValue(SlidingPanelBreak, {
    ssr: false,
  })
  const shouldShow = slidingPanelBreak && !hidden

  const handleToggleClick = () => {
    switch (clickBehavior) {
      case 'always-open':
        setTogglePanelState((prev) => {
          return { ...prev, isOpen: true }
        })
        break
      case 'always-close':
        setTogglePanelState((prev) => {
          return { ...prev, isOpen: false }
        })
        break
      default:
        setTogglePanelState((prev) => {
          return { ...prev, isOpen: !prev.isOpen }
        })
        break
    }
  }
  let toggleIcon: React.FC<React.SVGProps<SVGSVGElement>>
  let tooltipText: string
  switch (clickBehavior) {
    case 'always-open':
      toggleIcon = IconPanelShow
      tooltipText = 'Show List'
      break
    case 'always-close':
      toggleIcon = IconPanelHide
      tooltipText = 'Hide List'
      break
    default:
      toggleIcon = togglePanelState.isOpen ? IconPanelHide : IconPanelShow
      tooltipText = togglePanelState.isOpen ? 'Hide List' : 'Show List'
      break
  }
  if (togglePanelState.isDisabled) {
    toggleIcon = IconPanelHide
    tooltipText = ''
  }
  const ariaText = `${tooltipText} View`

  const variant = togglePanelState.isDisabled
    ? ToggleListButtonVariants.Disabled
    : ToggleListButtonVariants.Default
  const styles = useToggleListButtonStyles({ variant })

  if (shouldShow) {
    return (
      <Flex __css={styles.container}>
        <Tooltip
          hasArrow
          placement="right"
          label={tooltipText}
          bg={TooltipColors.Background}
          color={TooltipColors.FontColor}
        >
          <IconButton
            __css={styles.iconButton}
            {...rest}
            aria-label={ariaText}
            onClick={handleToggleClick}
          >
            <Icon __css={styles.icon} as={toggleIcon} />
          </IconButton>
        </Tooltip>
      </Flex>
    )
  }

  return <></>
}

interface StaticListPanelProps extends BoxProps {}
export const StaticListPanel = ({
  children,
  ...rest
}: StaticListPanelProps) => {
  const styles = useListPanelStyles()

  return (
    <Flex __css={styles.staticContainer} {...rest}>
      {children}
    </Flex>
  )
}

interface SlidingListPanelProps extends BoxProps {
  isOpen: boolean
  onToggleVisible?: () => void
}
export const SlidingListPanel = ({
  isOpen,
  onToggleVisible,
  children,
  ...rest
}: SlidingListPanelProps) => {
  const flexRef = React.useRef<HTMLDivElement>(null)

  useUserClickedOutsideElementBoundingBox(flexRef.current, () => {
    if (!isOpen) {
      return
    }
    onToggleVisible?.()
  })

  const styles = useListPanelStyles({
    variant: isOpen ? ListPanelVariants.Open : ListPanelVariants.Closed,
  })

  return (
    <Flex
      ref={flexRef}
      position="absolute"
      __css={styles.slidingContainer}
      {...rest}
    >
      {children}
    </Flex>
  )
}

export const DetailPanel = ({ children, ...rest }: BoxProps) => {
  const style = useDetailPanelStyle()
  return (
    <Flex sx={style} {...rest}>
      {children}
    </Flex>
  )
}

interface ResponsivePageProps extends BoxProps {
  header?: ReactNode
  quickActionButtons?: JSX.Element[]
  listPanel: ReactNode | (() => ReactNode)
  detailPanel?: ReactNode | (() => ReactNode)
  onToggle?: (isOpen: boolean) => void
  listPanelAlwaysOpen?: boolean
}
export const ResponsivePage = ({
  header,
  quickActionButtons,
  listPanel,
  detailPanel,
  onToggle,
  listPanelAlwaysOpen,
  ...rest
}: ResponsivePageProps) => {
  const slidingPanelBreak = useBreakpointValue(SlidingPanelBreak, {
    ssr: false,
  })
  const [togglePanelState, setTogglePanelState] = useAtom(togglePanelAtom)
  const handleToggleClick = () => {
    const newOpenState = !togglePanelState.isOpen
    setTogglePanelState((prev) => {
      return { ...prev, isOpen: newOpenState }
    })
    onToggle?.(newOpenState)
  }

  const listPanelContent = useMemo(() => {
    if (typeof listPanel === 'function') {
      return listPanel()
    }

    return listPanel
  }, [listPanel])

  const detailPanelContent = useMemo(() => {
    if (typeof detailPanel === 'function') {
      return detailPanel()
    }

    return detailPanel
  }, [detailPanel])

  const shouldShowStaticPanel = listPanelAlwaysOpen || !slidingPanelBreak

  return (
    <Flex width="full" position="relative" {...rest}>
      {shouldShowStaticPanel && (
        <StaticListPanel>{listPanelContent}</StaticListPanel>
      )}
      {!shouldShowStaticPanel && (
        <SlidingListPanel
          isOpen={togglePanelState.isOpen}
          onToggleVisible={handleToggleClick}
        >
          {listPanelContent}
        </SlidingListPanel>
      )}
      <DetailPanel>
        {header && (
          <Header>
            {header}
            <Spacer />
            {quickActionButtons && (
              <QuickActionStack>
                {quickActionButtons.map((qab) => {
                  return qab
                })}
              </QuickActionStack>
            )}
          </Header>
        )}
        {detailPanelContent}
      </DetailPanel>
    </Flex>
  )
}
